// frontend/src/contexts/ModalContext.jsx
import React, { createContext, useState, useContext, useCallback } from 'react';

// FIX: Add 'export' here so ModalContext is actually exported by name
export const ModalContext = createContext({
  modalType: null,
  modalProps: {},
  openModal: (type, props) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
  closeModal: () => {},
  isModalOpen: (type) => false, // eslint-disable-line @typescript-eslint/no-unused-vars
});

/**
 * Provides a global way to open and close different types of modals.
 * The actual rendering of the <Modal> component and its content
 * is typically handled by a top-level component (e.g., App.jsx or a GlobalModals.jsx)
 * that consumes this context.
 */
export const ModalProvider = ({ children }) => {
  const [modalType, setModalType] = useState(null);
  const [modalProps, setModalProps] = useState({});

  const openModal = useCallback((type, props = {}) => {
    console.log(`ModalContext: Opening modal. Type: ${type}`, props);
    setModalType(type);
    setModalProps(props);
  }, []);

  const closeModal = useCallback(() => {
    console.log(`ModalContext: Closing modal. Type: ${modalType}`);
    setModalType(null);
    setModalProps({});
  }, [modalType]);

  const isModalOpen = useCallback((type) => {
    return modalType === type;
  }, [modalType]);

  const contextValue = React.useMemo(() => ({
    modalType,
    modalProps,
    openModal,
    closeModal,
    isModalOpen,
  }), [modalType, modalProps, openModal, closeModal, isModalOpen]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};

/**
 * Custom hook to easily access modal control functions (openModal, closeModal)
 * and state (modalType, modalProps, isModalOpen).
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined || context === null) {
    throw new Error('useModal must be used within a ModalProvider. Make sure <ModalProvider> wraps your App.');
  }
  return context;
};